import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowTrendUp,
  faFileInvoiceDollar,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import {
  formatDateTime,
  formatKes,
  getMonthKey,
  getMonthLabel,
  summarizeFixedBills
} from '../../utils/financeWorkflow';

function PayBillsComp() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodKey, setPeriodKey] = useState(getMonthKey());

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const [vendorSnapshot, branchSnapshot, billSnapshot, paymentSnapshot] = await Promise.all([
          getDocs(collection(db, 'Vendors')),
          getDocs(collection(db, 'Branches')),
          getDocs(collection(db, 'Bills')),
          getDocs(collection(db, 'BillPayments'))
        ]);

        setVendors(vendorSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setBranches(branchSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setPayments(paymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (error) {
        console.error('Failed to load pay bills page:', error);
        swal('Error', 'Bill balances could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const vendorsById = useMemo(() => {
    return vendors.reduce((accumulator, vendor) => {
      accumulator[vendor.id] = vendor;
      return accumulator;
    }, {});
  }, [vendors]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const billRows = useMemo(() => {
    return summarizeFixedBills({
      bills,
      vendorsById,
      branchesById: branchMap,
      payments,
      periodKey
    }).sort((first, second) => {
      if (second.remainingAmount !== first.remainingAmount) {
        return second.remainingAmount - first.remainingAmount;
      }

      return second.expectedAmount - first.expectedAmount;
    });
  }, [bills, branchMap, payments, periodKey, vendorsById]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return billRows.filter((bill) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [bill.billTitle, bill.vendorName, bill.branchName, bill.vendorServices]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [billRows, searchTerm]);

  const summary = useMemo(() => {
    return billRows.reduce(
      (accumulator, bill) => {
        accumulator.expected += bill.expectedAmount;
        accumulator.paid += bill.paidAmount;
        accumulator.remaining += bill.remainingAmount;
        return accumulator;
      },
      {
        expected: 0,
        paid: 0,
        remaining: 0
      }
    );
  }, [billRows]);

  const recentPayments = useMemo(() => {
    return payments
      .filter((payment) => payment.billId)
      .sort((first, second) => {
        const firstTime = first.paymentDate?.seconds || first.createdAt?.seconds || 0;
        const secondTime = second.paymentDate?.seconds || second.createdAt?.seconds || 0;
        return secondTime - firstTime;
      })
      .slice(0, 12);
  }, [payments]);

  const openTopOutstandingBill = () => {
    const nextBill = billRows.find((bill) => bill.remainingAmount > 0) || billRows[0];

    if (!nextBill) {
      swal('No fixed bills', 'Add a fixed bill first, then open it to record payments.', 'info');
      return;
    }

    navigate(`/view-fixed-bill/${nextBill.id}`);
  };

  return (
    <div className="teamsComponent opsWorkspace">
      <section className="opsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Finance workflow</span>
          <h3>Pay bills</h3>
          <p>
            Use this page to spot what is still outstanding, then open the bill itself to record
            payments and review its history.
          </p>
        </div>
        <button type="button" className="opsPrimaryBtn" onClick={openTopOutstandingBill}>
          <FontAwesomeIcon icon={faWallet} /> Open next bill
        </button>
      </section>

      <section className="opsStatsGrid">
        <article className="opsStatCard">
          <span>{getMonthLabel(periodKey)} expected</span>
          <strong>{formatKes(summary.expected)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Paid this period</span>
          <strong>{formatKes(summary.paid)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Outstanding this period</span>
          <strong>{formatKes(summary.remaining)}</strong>
        </article>
      </section>

      <section className="opsLayout">
        <article className="opsPanel opsPanel--wide">
          <div className="opsToolbar">
            <div>
              <h4>Outstanding bill balances</h4>
              <p>Choose a month, review the live balance, then open the bill to pay it from its own page.</p>
            </div>
            <div className="opsFilters">
              <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search bill or vendor..."
              />
            </div>
          </div>

          {loading ? (
            <div className="opsLoadingCard">
              <Spinner animation="border" role="status" />
              <span>Loading bill balances...</span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="opsEmptyState">
              <h6>No bills available</h6>
              <p>Add fixed bills first, then open them from here to register payments.</p>
            </div>
          ) : (
            <div className="opsTableWrap">
              <table className="opsTable">
                <thead>
                  <tr>
                    <th>Bill</th>
                    <th>Expected</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((bill) => (
                    <tr key={bill.id}>
                      <td data-label="Bill">
                        <div className="opsDetailStack">
                          <strong>{bill.billTitle}</strong>
                          <small>{bill.vendorName} • {bill.branchName}</small>
                        </div>
                      </td>
                      <td data-label="Expected">{formatKes(bill.expectedAmount)}</td>
                      <td data-label="Paid">{formatKes(bill.paidAmount)}</td>
                      <td data-label="Remaining">{formatKes(bill.remainingAmount)}</td>
                      <td data-label="Status">
                        <span className={`opsStatusPill opsStatusPill--${bill.paymentTone}`}>{bill.paymentStatus}</span>
                      </td>
                      <td data-label="Open">
                        <button type="button" className="opsGhostBtn" onClick={() => navigate(`/view-fixed-bill/${bill.id}`)}>
                          <FontAwesomeIcon icon={faArrowRight} /> Open bill
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>

        <article className="opsPanel">
          <div className="opsSectionHeader">
            <div className="opsSectionIcon">
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
            </div>
            <div>
              <h4>Recent bill payments</h4>
              <p>Latest entries across all fixed bills.</p>
            </div>
          </div>

          {recentPayments.length === 0 ? (
            <div className="opsEmptyState opsEmptyState--compact">
              <h6>No bill payments recorded yet</h6>
              <p>Your latest fixed bill payments will appear here once the first one is saved.</p>
            </div>
          ) : (
            <div className="opsList">
              {recentPayments.map((payment) => (
                <div key={payment.id} className="opsListItem">
                  <div>
                    <strong>{payment.billTitle || 'Fixed bill payment'}</strong>
                    <small>{payment.vendorName || payment.payee || 'Vendor'} • {payment.billingPeriodLabel || 'Current period'}</small>
                  </div>
                  <div className="opsListItemAside">
                    <strong>{formatKes(payment.amount)}</strong>
                    <small>{formatDateTime(payment.paymentDate)}</small>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="opsInsightCard">
            <div className="opsInsightIcon">
              <FontAwesomeIcon icon={faArrowTrendUp} />
            </div>
            <div>
              <strong>Payments now live inside each bill</strong>
              <p>
                Open the bill to see its summary, monthly balance, and full payment history before
                recording another payment.
              </p>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}

export default PayBillsComp;
